/**
 * Tenant-scoped customer-tag CRUD. Tags drive both customer-list
 * segmentation (future) and one half of the customer-field scope.
 *
 * Endpoints: `/api/v1/customer-tags/*` — see `CustomerTagController`.
 */
import { useQuery } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";

export type CustomerTag = {
  id: string;
  name: string;
  color: string | null;
};

export type CustomerTagsResponse = { tags: CustomerTag[] };

export function useCustomerTags() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["customer.tags"],
    enabled: !!accessToken,
    queryFn: () =>
      authFetchJson<CustomerTagsResponse>(`/api/v1/customer-tags`),
  });
}

export type CustomerTagInput = { name: string; color?: string };

export async function createCustomerTag(
  body: CustomerTagInput,
): Promise<CustomerTag> {
  return authFetchJson<CustomerTag>(`/api/v1/customer-tags`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateCustomerTag(
  tagId: string,
  body: Partial<CustomerTagInput>,
): Promise<CustomerTag> {
  return authFetchJson<CustomerTag>(`/api/v1/customer-tags/${tagId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteCustomerTag(tagId: string): Promise<void> {
  const res = await authFetch(`/api/v1/customer-tags/${tagId}`, {
    method: "DELETE",
  });
  await throwIfNotOk(res);
}
